import type { HelpGuide } from '../types';

export const reportsGuide: HelpGuide = {
  key: 'reports',
  title: 'งบการเงิน (Financial Reports)',
  subtitle: 'เมนู: รายงานและภาษี ▸ งบการเงิน',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/reports/คู่มือการใช้งาน_งบการเงิน_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “งบการเงิน (Financial Reports)” ใช้ดูงบการเงินของบริษัทแบบสำเร็จรูปจากข้อมูลในบัญชีแยกประเภท (GL) โดยตรง เป็นหน้าแสดงผลอย่างเดียว (อ่านอย่างเดียว) ไม่มีการสร้าง/แก้ไข/อนุมัติเอกสารใด ๆ เลือกงวด (เดือน) ที่หัวข้อ แล้วระบบจะรวมยอดจากใบสำคัญที่ลงบัญชีไว้มาจัดเป็นงบให้อัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'แนวคิด: ทุกงบในหน้านี้คำนวณจากใบสำคัญที่ “ลงบัญชีแล้ว” ในงวดที่เลือก — งบแสดงฐานะการเงินเป็นยอดคงเหลือ ณ สิ้นเดือน · งบกำไรขาดทุน/งบกระแสเงินสดเป็นยอดของงวด (ทั้งเดือน) · งบทดลองเป็นความเคลื่อนไหวเดบิต/เครดิตของงวดพร้อมตรวจสมดุล Dr = Cr',
        },
        { kind: 'para', text: 'หน้าจอมี 4 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'งบแสดงฐานะการเงิน — ',
              text: 'สินทรัพย์/หนี้สิน/ส่วนของผู้ถือหุ้น ณ สิ้นงวด พร้อมเปรียบเทียบงวดก่อน (แท็บเริ่มต้น)',
            },
            {
              lead: 'งบกำไรขาดทุน — ',
              text: 'รายได้/ต้นทุน/ค่าใช้จ่าย และกำไรสุทธิของงวด',
            },
            {
              lead: 'งบกระแสเงินสด — ',
              text: 'กระแสเงินสดจากกิจกรรมดำเนินงาน/ลงทุน/จัดหาเงิน',
            },
            {
              lead: 'งบทดลอง (Trial Balance) — ',
              text: 'เดบิต/เครดิตและยอดคงเหลือรายบัญชี พร้อมตรวจสมดุล Dr = Cr',
            },
          ],
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'หน้านี้ไม่มีการ์ดสรุปยอด (KPI) แสดงเป็นตารางงบล้วน ๆ ที่หัวข้อด้านบนมีตัวกรองและปุ่มที่ใช้ร่วมกันทุกแท็บ:',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ตัวเลือกงวด (เดือน) — ',
              text: 'ช่องเลือกเดือน (ปี-เดือน) ใช้กำหนดงวดของงบ ค่าเริ่มต้นเป็นเดือนปัจจุบัน',
            },
            {
              lead: 'สาขา: ทั้งหมด — ',
              text: 'กรองงบเฉพาะสาขาเดียว หรือเลือก “ทั้งหมด” เพื่อรวมทุกสาขา',
            },
            {
              lead: 'หน่วยงาน: ทั้งหมด — ',
              text: 'กรองงบเฉพาะหน่วยงาน/ศูนย์ต้นทุนเดียว หรือ “ทั้งหมด”',
            },
            {
              lead: 'เปรียบเทียบ: งวดก่อน — ',
              text: 'คอลัมน์เปรียบเทียบใช้ “งวดก่อน” (เดือนก่อนหน้า) เสมอ ตัวเลือก “ปีก่อน/งบประมาณ” ในช่องนี้ยังไม่เปิดใช้งาน (กำลังพัฒนา)',
            },
            {
              lead: 'พิมพ์ / Excel — ',
              text: 'ปุ่มสั่งพิมพ์และส่งออก Excel ปรากฏที่ส่วนหัว แต่ฟังก์ชันส่งออกยังไม่เปิดใช้งาน (กำลังพัฒนา)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'หากงวดที่เลือกยังไม่มีรายการลงบัญชี ระบบจะแสดงข้อความสถานะแทนตาราง: “กำลังโหลด…” ขณะดึงข้อมูล · “ไม่มีรายการ” เมื่อไม่มีข้อมูลในงวดนั้น · “โหลดล้มเหลว: …” เมื่อเกิดข้อผิดพลาด',
        },
      ],
    },
    {
      id: 'balance-sheet',
      heading: '3. งบแสดงฐานะการเงิน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ งบแสดงฐานะการเงิน แสดงยอดคงเหลือ ณ สิ้นงวดที่เลือก แบ่งเป็น สินทรัพย์ · หนี้สิน · ส่วนของผู้ถือหุ้น จัดเป็นลำดับชั้น (หัวข้อกลุ่ม / รายการบัญชี / ยอดรวม) คอลัมน์: รหัส · รายการ · ยอดงวดนี้ (สิ้นเดือนที่เลือก) · ยอดงวดเปรียบเทียบ · เปลี่ยนแปลง · %',
        },
        {
          kind: 'para',
          text: 'คอลัมน์ เปลี่ยนแปลง คือผลต่างจากงวดเปรียบเทียบ (สีเขียวเมื่อเพิ่ม สีแดงเมื่อลด) และคอลัมน์ % คือร้อยละของการเปลี่ยนแปลง ส่วนท้ายตารางระบุ “หน่วย: บาท · งบรวมก่อนตรวจสอบ (Unaudited)” และ “จัดทำโดย: ฝ่ายบัญชี”',
        },
        {
          kind: 'image',
          src: '/help/reports/01-balance-sheet.png',
          caption:
            'งบแสดงฐานะการเงิน — สินทรัพย์/หนี้สิน/ส่วนของผู้ถือหุ้น พร้อมคอลัมน์เปรียบเทียบงวดก่อนและการเปลี่ยนแปลง %',
        },
      ],
    },
    {
      id: 'income-statement',
      heading: '4. งบกำไรขาดทุน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ งบกำไรขาดทุน แสดงผลการดำเนินงานของงวดที่เลือก จัดโครงสร้าง รายได้ · ต้นทุนขาย · ค่าใช้จ่าย และสรุปเป็น กำไรสุทธิ ใช้คอลัมน์เดียวกับงบแสดงฐานะการเงิน (รหัส · รายการ · ยอดงวดนี้ · ยอดงวดเปรียบเทียบ · เปลี่ยนแปลง · %) ส่วนท้ายระบุ “หน่วย: บาท · งวด [เดือนที่เลือก]”',
        },
        {
          kind: 'para',
          text: 'กรณีขาดทุนสุทธิ ยอดกำไรสุทธิจะแสดงเป็นค่าติดลบ (สีแดง) สามารถกรองตามสาขา/หน่วยงานที่หัวข้อด้านบนได้เช่นเดียวกับทุกแท็บ',
        },
        {
          kind: 'image',
          src: '/help/reports/02-income-statement.png',
          caption: 'งบกำไรขาดทุน — รายได้/ต้นทุน/ค่าใช้จ่าย สรุปกำไรสุทธิ พร้อมเปรียบเทียบงวดก่อน',
        },
      ],
    },
    {
      id: 'cash-flow',
      heading: '5. งบกระแสเงินสด',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ งบกระแสเงินสด แสดงการเคลื่อนไหวของเงินสดในงวด แบ่งเป็น 3 กิจกรรม: กิจกรรมดำเนินงาน · กิจกรรมลงทุน · กิจกรรมจัดหาเงิน แต่ละกลุ่มมียอดรวมย่อย และสรุปเป็นการเปลี่ยนแปลงสุทธิของเงินสด ใช้คอลัมน์ชุดเดียวกับงบกำไรขาดทุน (มีงวดนี้และงวดเปรียบเทียบ) แต่แท็บนี้ไม่มีแถบส่วนท้าย (footer) แสดงหน่วย/งวด',
        },
        {
          kind: 'image',
          src: '/help/reports/03-cash-flow.png',
          caption:
            'งบกระแสเงินสด — กิจกรรมดำเนินงาน/ลงทุน/จัดหาเงิน พร้อมยอดรวมย่อยและการเปลี่ยนแปลงสุทธิ',
        },
      ],
    },
    {
      id: 'trial-balance',
      heading: '6. งบทดลอง (Trial Balance)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ งบทดลอง แสดงความเคลื่อนไหวเดบิต/เครดิตและยอดคงเหลือของทุกบัญชีในงวด คอลัมน์: รหัส · ชื่อบัญชี · ประเภท (ASSET/LIABILITY/EQUITY/REVENUE/EXPENSE/COST) · เดบิต (เคลื่อนไหว) · เครดิต (เคลื่อนไหว) · ยอดคงเหลือ (พร้อมป้าย Dr หรือ Cr ตามธรรมชาติของบัญชี) ค่าที่เป็น 0 จะแสดงเป็น “—”',
        },
        {
          kind: 'para',
          text: 'ใต้ช่องเลือกงวดมีป้ายสถานะสมดุล: สีเขียว “สมดุล Dr = Cr” เมื่อยอดเดบิตรวมเท่ายอดเครดิตรวม หรือสีแดง “ไม่สมดุล Dr ≠ Cr” เมื่อไม่เท่ากัน แถวสุดท้ายของตารางแสดง “รวม” พร้อมยอดเดบิตรวม/เครดิตรวม และในคอลัมน์ยอดคงเหลือจะแสดงสถานะแบบย่อ “✓ สมดุล” (เขียว) หรือ “✗ ไม่สมดุล” (แดง) — ข้อความเต็ม “Dr = Cr” อยู่ที่ป้ายเหนือตาราง ส่วนท้ายระบุ “งวด YYYY-MM · [จำนวน] บัญชี”',
        },
        {
          kind: 'image',
          src: '/help/reports/04-trial-balance.png',
          caption:
            'งบทดลอง — เดบิต/เครดิต (เคลื่อนไหว) และยอดคงเหลือรายบัญชี พร้อมป้ายสถานะ “สมดุล Dr = Cr”',
        },
      ],
    },
    {
      id: 'trial-balance-group',
      heading: '7. งบทดลองจัดกลุ่มตามบัญชี + สาขา',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บงบทดลองสามารถเลือกการจัดกลุ่ม (groupBy) ได้ 3 แบบ: ตามบัญชี · ตามบัญชี + สาขา · ตามบัญชี + หน่วยงาน เมื่อเลือกแบบมีมิติ (สาขาหรือหน่วยงาน) ระบบจะเพิ่มคอลัมน์มิติ และแยกเป็น 1 แถวต่อ (บัญชี + มิติ) บัญชีที่ไม่มีมิติจะแสดง “ไม่ระบุ”',
        },
        {
          kind: 'para',
          text: 'การจัดกลุ่มแบบนี้ช่วยให้เห็นยอดเคลื่อนไหวของแต่ละบัญชีแยกตามสาขา (หรือหน่วยงาน) ในตารางเดียว เหมาะสำหรับการกระทบยอดและวิเคราะห์ตามมิติรายงาน',
        },
        {
          kind: 'image',
          src: '/help/reports/05-trial-balance-by-branch.png',
          caption: 'งบทดลองจัดกลุ่ม “ตามบัญชี + สาขา” — เพิ่มคอลัมน์สาขา แยก 1 แถวต่อบัญชี+สาขา',
        },
      ],
    },
    {
      id: 'trial-balance-filter',
      heading: '8. งบทดลองกรองเฉพาะสาขา',
      blocks: [
        {
          kind: 'para',
          text: 'นอกจากการจัดกลุ่ม ยังกรองงบทดลองให้แสดงเฉพาะสาขาเดียวได้ โดยเลือกสาขาที่ตัวกรอง “สาขา” ในส่วนหัว (เช่น เลือกสาขาสำนักงานใหญ่) ตารางจะแสดงเฉพาะรายการของสาขานั้น ทำให้จำนวนแถวน้อยลงและตรวจสอบยอดเฉพาะสาขาได้ง่ายขึ้น',
        },
        {
          kind: 'para',
          text: 'ตัวกรองสาขา/หน่วยงานในส่วนหัวใช้ได้กับทุกแท็บ (งบแสดงฐานะการเงิน/งบกำไรขาดทุน/งบกระแสเงินสด/งบทดลอง) เป็นการกรองระดับรายการบรรทัด ไม่ใช่แค่ยอดรวม',
        },
        {
          kind: 'image',
          src: '/help/reports/06-trial-balance-filtered-branch.png',
          caption:
            'งบทดลองกรองเฉพาะสาขา (เลือกจากตัวกรองสาขาในส่วนหัว) — แสดงเฉพาะรายการของสาขาที่เลือก',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '9. สรุปสถานะและสัญลักษณ์',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ/สัญลักษณ์', 'ความหมาย'],
          rows: [
            [
              'สมดุล Dr = Cr (ป้ายเขียว) / ✓ สมดุล (แถวรวม)',
              'ยอดเดบิตรวมเท่ากับยอดเครดิตรวม — งบทดลองถูกต้อง',
            ],
            [
              'ไม่สมดุล Dr ≠ Cr (ป้ายแดง) / ✗ ไม่สมดุล (แถวรวม)',
              'ยอดเดบิตรวมไม่เท่ายอดเครดิตรวม — ต้องตรวจสอบการลงบัญชี',
            ],
            ['Dr / Cr (ท้ายยอดคงเหลือ)', 'ระบุธรรมชาติของยอดคงเหลือ (เดบิต/เครดิต) ตามประเภทบัญชี'],
            ['— (ขีด)', 'ค่าที่เป็นศูนย์ (ไม่มีความเคลื่อนไหว)'],
            ['ไม่ระบุ', 'รายการที่ไม่มีสาขา/หน่วยงาน เมื่อจัดกลุ่มตามมิติ'],
            ['กำลังโหลด…', 'ระบบกำลังดึงข้อมูลงบ'],
            ['ไม่มีรายการ', 'งวดที่เลือกไม่มีรายการลงบัญชี'],
            ['โหลดล้มเหลว: …', 'เกิดข้อผิดพลาดขณะดึงข้อมูล'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมงวดที่เลือกถึงขึ้น “ไม่มีรายการ”?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'งวดนั้นยังไม่มีใบสำคัญที่ลงบัญชี (เช่น เดือนในอนาคต) งบจะแสดงเมื่อมีรายการในงวด',
            },
          ],
        },
        { kind: 'para', text: 'งบทดลองขึ้น “ไม่สมดุล Dr ≠ Cr” ต้องทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [{ text: 'ตรวจสอบใบสำคัญในงวดนั้น ยอดเดบิตรวมต้องเท่ายอดเครดิตรวมเสมอ' }],
        },
        { kind: 'para', text: 'ทำไมยอดในงบเปลี่ยนเมื่อเลือกสาขา/หน่วยงาน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ตัวกรองสาขา/หน่วยงานจะกรองงบให้เหลือเฉพาะมิติที่เลือก หากต้องการดูทั้งบริษัทให้เลือก “ทั้งหมด”',
            },
          ],
        },
        { kind: 'para', text: 'หน้านี้แก้ไขตัวเลขได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ได้ หน้านี้เป็นการแสดงผลอย่างเดียว ตัวเลขมาจากการลงบัญชีจริง หากต้องแก้ต้องไปแก้ที่ใบสำคัญต้นทาง',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'งบแสดงฐานะการเงิน — ',
              text: 'งบสรุปสินทรัพย์ หนี้สิน และส่วนของผู้ถือหุ้น ณ วันสิ้นงวด',
            },
            {
              lead: 'งบกำไรขาดทุน — ',
              text: 'งบสรุปรายได้ ต้นทุน ค่าใช้จ่าย และกำไรสุทธิของงวด',
            },
            {
              lead: 'งบกระแสเงินสด — ',
              text: 'งบแสดงการเคลื่อนไหวเงินสดจากกิจกรรมดำเนินงาน/ลงทุน/จัดหาเงิน',
            },
            {
              lead: 'งบทดลอง (Trial Balance) — ',
              text: 'รายงานเดบิต/เครดิตและยอดคงเหลือรายบัญชี ใช้ตรวจสมดุล Dr = Cr',
            },
            {
              lead: 'Dr / Cr — ',
              text: 'เดบิต (Debit) / เครดิต (Credit) ระบุธรรมชาติของยอดบัญชี',
            },
            {
              lead: 'มิติรายงาน (สาขา/หน่วยงาน) — ',
              text: 'การแบ่งยอดตามสาขาหรือหน่วยงาน/ศูนย์ต้นทุน เพื่อกรองหรือจัดกลุ่ม',
            },
            {
              lead: 'งวดเปรียบเทียบ — ',
              text: 'คอลัมน์อ้างอิงสำหรับเทียบ ปัจจุบันใช้ “งวดก่อน” (เดือนก่อนหน้า) เสมอ',
            },
          ],
        },
      ],
    },
  ],
};
